import { isEqual, kebabCase, upperFirst } from 'lodash';
import React, { memo, useCallback, useEffect, useRef } from 'react';
import { useSelector, useStore } from 'react-redux';

import VisibilityTracker from '@swf/components/VisibilityTracker/VisibilityTracker';
import { TrackingCategory } from '@swf/core/enums';
import StarsWidgetContext from '@swf/core/providers/StarsWidgetContext';
import type { TrackingContextValue } from '@swf/core/providers/TrackingContext';
import TrackingContext from '@swf/core/providers/TrackingContext';
import type { RootState, Store } from '@swf/core/store';
import type { WidgetContainer, WidgetInstanceProps } from '@swf/core/types/Widget';
import { fetchProperties } from '@swf/core/utils/widgets';
import widgets from '@swf/widgets/index';

import { useSubscriptions } from './hooks/useSubscriptions';
import { useUpdateWidgetState } from './hooks/useUpdateWidgetState';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getSelector = (store: Store, id: string, key = 'get', ...args: any[]) => {
  if (!store.select[id] || !store.select[id][key]) return () => undefined;
  return (state: RootState) => store.select[id][key](state, ...args);
};

const StarsWidget: React.FC<WidgetContainer> = (widget) => {
  const { id, meta } = widget;
  const store = useStore() as Store;
  const Widget = widgets[widget.type] as React.FC<WidgetInstanceProps>;
  const data = useSelector(getSelector(store, id));
  const dispatch = store.dispatch[id];
  const updateSubscriptions = useSubscriptions(widget);
  const updateWidgetState = useUpdateWidgetState(widget);

  let category = upperFirst(kebabCase(widget.type).split('-')[0]) as TrackingCategory;
  category = [TrackingCategory.Casino, TrackingCategory.Sports].includes(category)
    ? category
    : TrackingCategory.Stars;

  const { current: trackingContext } = useRef<TrackingContextValue>({
    category,
    widget: { widgetName: meta.name, widgetId: id }
  });

  useEffect(() => {
    fetchProperties(widget.type).then(({ trackingReducer }) => {
      if (trackingReducer) {
        trackingContext.entities = trackingReducer(widget.services, data, widget.config);
      }
    });
  }, [id, widget, trackingContext, store.select, data]);

  const fetchService = useCallback(
    (key, params, meta) => dispatch.fetch?.({ key, params, meta, refresh: true }),
    [dispatch]
  );

  if (!dispatch) throw new Error(`Widget model for "${id}" not preloaded into store`);

  return (
    <StarsWidgetContext.Provider
      value={{ widgetId: id, widgetConfig: widget.config, updateWidgetState }}
    >
      <TrackingContext.Provider value={trackingContext}>
        <VisibilityTracker name={widget.meta.name}>
          <Widget
            clear={dispatch.clear}
            config={widget.config}
            data={data}
            fetchService={fetchService}
            id={id}
            preload={widget.preload}
            services={widget.services}
            style={widget.layout}
            updateState={updateWidgetState}
            updateSubscriptions={updateSubscriptions}
          />
        </VisibilityTracker>
      </TrackingContext.Provider>
    </StarsWidgetContext.Provider>
  );
};

export default memo(StarsWidget, isEqual);
